import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { TAddEditHealthSystemData } from '../../types'
import { setLoading } from '../../../../../../redux/store/common/slice'
import { getHealthSystemData } from '../../../api'
import { getUser } from 'redux/store/user/getters'
import useTabs, {
  TabsReturnActions,
  TabsReturnState
} from '../../../../../../hooks/useTabs'
import { CREATE_EDIT_HS_TABS, CREATE_EDIT_HS_TABS_KEYS } from '../../constants'
import { getGpoDetails } from '../../../../HealthSystems/HealthSystemDetails/api'
import {
  ability,
  ACTIONS,
  SUBJECTS,
  SubjectWithAttributes
} from '../../../../../Permission'
import { subject } from '@casl/ability'
import { defineAbility } from 'features/MyCommunity/MyOrganizationCommunity/Abilities'

import {
  ACTIONS as MY_COMMUNITY_ACTIONS,
  SUBJECTS as MY_COMMUNITY_SUBJECTS
} from 'features/MyCommunity/MyOrganizationCommunity/Abilities/constants'
import { myOrganizationCommunitySubject } from 'features/MyCommunity/MyOrganizationCommunity/Abilities/types'
import { TBusinessAccountDetails } from 'features/MyCommunity/MyOrganizationCommunity/BankingSetup/types'
import {
  getBusinessAccountDetailsRequest,
  getCommunityAccessOptionsRequest
} from 'features/MyCommunity/MyOrganizationCommunity/BankingSetup/api'
import { updateLoadingStack } from 'redux/store/common/slice'
import { FETCH_STATUS } from 'redux/store/common/types'
import { TOptionsResponse } from 'constants/types'

type ContextProps = {
  state: {
    details: TAddEditHealthSystemData | null
    businessAccountDetails: TBusinessAccountDetails | undefined
    communityAccessOptions: TOptionsResponse[] | null
    canEditHS: boolean
    canViewHospitals: boolean
    canViewBankingSetup: boolean
    canViewBankAccounts: boolean
    canEditCommunityAccess: boolean
  } & TabsReturnState
  actions: TabsReturnActions & {
    getHSDetails: () => Promise<void>
    getBusinessAccountDetails: () => Promise<void>
    refreshHSDetails: () => void
  }
}

const HSDetailsContext = createContext<ContextProps>({
  state: {} as ContextProps['state'],
  actions: {} as ContextProps['actions']
})

const ViewEditHSContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const dispatch = useDispatch()
  const { id: routeId, tab } = useParams()
  const user = useSelector(getUser)

  const [details, setDetails] = useState<TAddEditHealthSystemData | null>(null)
  const [businessAccountDetails, setBusinessAccountDetails] = useState<
    TBusinessAccountDetails | undefined
  >(undefined)
  const [communityAccessOptions, setCommunityAccessOptions] = useState<
    TOptionsResponse[] | null
  >(null)

  const myCommunityAbility = useMemo(() => defineAbility(user), [user])

  const canEditCommunityAccess =
    ability.can(ACTIONS.CRUD, SUBJECTS.COMMUNITY_ACCESS) &&
    details?.uuid === user.health_system

  const canEditHS =
    ability.can(ACTIONS.CRUD, SUBJECTS.HOSPITALS_IN_HS_DETAILS) ||
    (ability.can(ACTIONS.EDIT, SUBJECTS.HOSPITALS_IN_HS_DETAILS) &&
      details?.uuid === user.health_system)

  const canViewHospitals = useMemo(() => {
    return ability.can(
      ACTIONS.VIEW,
      subject(SUBJECTS.HOSPITALS_IN_HS_DETAILS, {
        ...details
      } as unknown as SubjectWithAttributes)
    )
  }, [details])

  const canViewUsers = ability.can(
    ACTIONS.VIEW,
    subject(SUBJECTS.USERS_IN_HS_DETAILS, {
      ...details
    } as unknown as SubjectWithAttributes)
  )

  const canViewBankingSetup = myCommunityAbility.can(
    MY_COMMUNITY_ACTIONS.VIEW,
    myOrganizationCommunitySubject(
      MY_COMMUNITY_SUBJECTS.BANKING_SETUP_HS_DETAILS,
      {
        uuid: details?.uuid
      }
    )
  )

  const canViewBankAccounts = myCommunityAbility.can(
    MY_COMMUNITY_ACTIONS.VIEW,
    myOrganizationCommunitySubject(
      MY_COMMUNITY_SUBJECTS.BANK_ACCOUNTS_HS_DETAILS,
      {
        uuid: details?.uuid,
        businessAccountStatus: businessAccountDetails?.verification_status
      }
    )
  )

  const availableTabs = useMemo(() => {
    return CREATE_EDIT_HS_TABS.filter(({ key }) => {
      switch (key) {
        case CREATE_EDIT_HS_TABS_KEYS.users:
          return canViewUsers
        case CREATE_EDIT_HS_TABS_KEYS.hospitals:
          return canViewHospitals
        case CREATE_EDIT_HS_TABS_KEYS.banking_setup:
          return canViewBankingSetup
        case CREATE_EDIT_HS_TABS_KEYS.bank_accounts:
          return canViewBankAccounts
        default:
          return true
      }
    })
  }, [canViewUsers, canViewHospitals, canViewBankingSetup, canViewBankAccounts])

  const { state: tabsState, actions: tabsActions } = useTabs({
    tabs: availableTabs,
    activeTab: tab || availableTabs[0].key,
    updateTrigger: availableTabs
  })

  const canSeeGPO = useMemo(() => {
    if (!details) return false
    return (
      ability.can(ACTIONS.VIEW, SUBJECTS.GPO_HS) &&
      user.health_system === details.uuid
    )
  }, [details, user.health_system])

  const fetchGpoDetails = useCallback(
    async (uuid) => {
      if (!uuid || !canSeeGPO) return
      const res = await getGpoDetails(uuid)
      if (res.data) {
        return res.data ?? undefined
      }
      return undefined
    },
    [canSeeGPO]
  )

  const getHSDetails = useCallback(async () => {
    if (routeId !== 'new') {
      dispatch(setLoading(true))
      try {
        const { data } = await getHealthSystemData(user.community, routeId)
        const gpoDetails = await fetchGpoDetails(routeId)
        setDetails({
          ...data,
          address: data.address ?? {
            state: '',
            city: '',
            street: '',
            zip_code: ''
          },
          gpo: gpoDetails,
          departments: data.added_departments
        })
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setLoading(false))
      }
    }
  }, [routeId, dispatch, user.community, fetchGpoDetails])

  const getBusinessAccountDetails = useCallback(async () => {
    if (!details?.moov_business_account) return
    try {
      dispatch(updateLoadingStack(FETCH_STATUS.REQUEST_STARTED))
      const res = await getBusinessAccountDetailsRequest(
        details.moov_business_account
      )
      setBusinessAccountDetails(res.data)
    } catch (error) {
      console.error(error)
    } finally {
      dispatch(updateLoadingStack(FETCH_STATUS.REQUEST_FINISHED))
    }
  }, [details?.moov_business_account, dispatch])

  const getCommunityAccessOptions = useCallback(async () => {
    if (!canEditCommunityAccess) return
    try {
      dispatch(updateLoadingStack(FETCH_STATUS.REQUEST_STARTED))
      const res = await getCommunityAccessOptionsRequest()
      setCommunityAccessOptions(res.data)
    } catch (error) {
      console.error(error)
    } finally {
      dispatch(updateLoadingStack(FETCH_STATUS.REQUEST_FINISHED))
    }
  }, [dispatch, canEditCommunityAccess])

  const refreshHSDetails = useCallback(() => {
    getHSDetails()
    getBusinessAccountDetails()
  }, [getHSDetails, getBusinessAccountDetails])

  useEffect(() => {
    getHSDetails()
    getCommunityAccessOptions()
  }, [getCommunityAccessOptions, getHSDetails])

  useEffect(() => {
    if (details?.moov_business_account) {
      getBusinessAccountDetails()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details?.moov_business_account])

  const context = useMemo(
    () => ({
      state: {
        ...tabsState,
        details,
        businessAccountDetails,
        canEditHS,
        canViewHospitals,
        canViewBankingSetup,
        canViewBankAccounts,
        canEditCommunityAccess,
        communityAccessOptions
      },
      actions: {
        ...tabsActions,
        getHSDetails,
        getBusinessAccountDetails,
        refreshHSDetails
      }
    }),
    [
      tabsState,
      details,
      businessAccountDetails,
      canEditHS,
      canViewHospitals,
      canViewBankingSetup,
      canViewBankAccounts,
      canEditCommunityAccess,
      communityAccessOptions,
      tabsActions,
      getHSDetails,
      getBusinessAccountDetails,
      refreshHSDetails
    ]
  )

  return (
    <HSDetailsContext.Provider value={context}>
      {children}
    </HSDetailsContext.Provider>
  )
}

export const useHSDetailsContext = () => useContext(HSDetailsContext)

export default ViewEditHSContextProvider
